import clsx from 'clsx';
import React, { useCallback, useEffect, useReducer, useRef } from 'react';

import ClickAwayListener from '@/src/components/common/ClickAwayListener/ClickAwayListener';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { cn } from '@/src/util/ui';

import Options from './Options/Options';
import SelectedTags from './SelectedTags/SelectedTags';

const INITIAL_TAG_INPUT = {
  page: 1,
  isInputFocused: false,
  showOptions: false,
};

const ACTIONS = {
  SET_PAGE: 'SET_PAGE',
  TOGGLE_SHOW_OPTIONS: 'TOGGLE_SHOW_OPTIONS',
  SET_INPUT_FOCUSED: 'SET_INPUT_FOCUSED',
  CLICK_DROPDOWN: 'CLICK_DROPDOWN',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_PAGE:
      return {
        ...state,
        page: action.page,
      };
    case ACTIONS.TOGGLE_SHOW_OPTIONS:
      return {
        ...state,
        showOptions: action.showOptions,
      };
    case ACTIONS.SET_INPUT_FOCUSED:
      return {
        ...state,
        isInputFocused: action.isInputFocused,
      };
    case ACTIONS.CLICK_DROPDOWN:
      return {
        ...state,
        page: 1,
        showOptions: true,
        isInputFocused: true,
      };
    default:
      return state;
  }
};

const UserTagInput = (props) => {
  const {
    tagKey,
    options,
    tags,
    setTags,
    validateInput,
    onClickDropdown,
    onInputChange,
    hasMore = false,
    onLoadMore = null,
    onBlur = null,
    classes,
  } = props;
  const editMode = {
    tagKey,
    options,
    hasMore,
    tags,
    validateInput,
    onInputChange,
    onLoadMore,
  };
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const popperRef = useRef(null);
  const [tagInput, dispatch] = useReducer(reducer, INITIAL_TAG_INPUT);
  const { showOptions, isInputFocused } = tagInput;
  const observer = useRef();

  const toggleShowOptions = useCallback((showOptions) => {
    dispatch({
      type: ACTIONS.TOGGLE_SHOW_OPTIONS,
      showOptions,
    });
  }, []);

  const setIsInputFocused = useCallback((isInputFocused) => {
    dispatch({
      type: ACTIONS.SET_INPUT_FOCUSED,
      isInputFocused,
    });
  }, []);

  const clickDropdown = useCallback(() => {
    dispatch({
      type: ACTIONS.CLICK_DROPDOWN,
    });
  }, []);

  const lastElementRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && onLoadMore) {
          onLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore, onLoadMore]
  );

  const handleKeyDown = useCallback(
    (e) => {
      const tagValue = e.target.value;

      if (!tagValue && !tags.length) {
        toggleShowOptions(false);
      }

      if (e.key === 'Backspace' && !tagValue) {
        setTags(tags.slice(0, -1));
      }

      if (e.key !== 'Enter' && e.key !== 'Tab') {
        return;
      }

      addTag(tagValue);
      toggleShowOptions(false);
    },
    [tags, setTags, addTag, toggleShowOptions]
  );

  const onChange = useCallback(
    (e) => {
      const inputValue = e.currentTarget.value.toLowerCase();

      onInputChange({ inputValue });

      if (inputValue) {
        toggleShowOptions(true);
      } else {
        toggleShowOptions(false);
      }
    },
    [onInputChange, toggleShowOptions]
  );

  const addTag = useCallback(
    (tagValue) => {
      const option = options.find((option) => option.value === tagValue);
      const optionTag = option
        ? {
            ...option,
            label: option.label,
            value: option.value,
          }
        : {
            label: tagValue,
            value: tagValue,
          };

      if (validateInput) {
        optionTag.valid = validateInput(tagValue);
      }

      if (tagValue.length > 0) {
        setTags([...tags, optionTag]);

        inputRef.current.value = '';
      }
    },
    [tags, options, setTags, validateInput]
  );

  const removeTag = useCallback(
    (removedTag) => {
      const newTags = tags.filter((tag) => tag !== removedTag);
      setTags(newTags);
    },
    [tags, setTags]
  );

  const handleUpdateTag = useCallback(
    ({ tags, oldTag, newTagValue }) => {
      const index = tags.findIndex((tag) => tag.value === oldTag.value);
      const newTags = [...tags];

      if (oldTag.value === newTagValue) {
        return;
      }

      let updatedTag = {
        label: newTagValue,
        value: newTagValue,
      };

      if (index >= 0) {
        const option = options.find((option) => option.value === newTagValue);

        if (option) {
          updatedTag = {
            ...option,
            label: option.label,
            value: option.value,
          };
        }

        if (validateInput) {
          updatedTag.valid = validateInput(newTagValue);
        }

        newTags.splice(index, 1, { ...tags[index], ...updatedTag });
      }

      setTags(newTags);
    },
    [setTags, options, validateInput]
  );

  const onSelectOption = useCallback(
    (selectOptions) => {
      const { option, checked } = selectOptions;

      if (checked) {
        const updatedTags = tags.filter((tag) => tag.value !== option.value);
        setTags(updatedTags);
      } else {
        addTag(option.value);
      }

      inputRef.current.value = '';
      inputRef.current.focus();
      setIsInputFocused(true);
    },
    [tags, setTags, inputRef, addTag, setIsInputFocused]
  );

  const handleDropDownClick = useCallback(async () => {
    if (onClickDropdown) {
      await onClickDropdown();
    }

    clickDropdown();
  }, [onClickDropdown, clickDropdown]);

  const onFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleOnBlur = useCallback(
    (e) => {
      if (containerRef && containerRef.current && containerRef.current.contains(e.target)) {
        return;
      }
      const currentValue = inputRef.current.value;
      addTag(inputRef.current.value);
      setIsInputFocused(false);
      onBlur?.(currentValue, tags);
    },
    [addTag, onBlur, setIsInputFocused, tags]
  );

  const handlePopperClickAway = useCallback((e) => {
    if (popperRef && popperRef.current && popperRef.current.contains(e.target)) {
      return;
    }

    toggleShowOptions(false);
  }, []);

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    if (isInputFocused) {
      inputRef.current.focus();
    }
  }, [isInputFocused]);

  return (
    <React.Fragment>
      <div
        className={cn(
          'container--tag',
          'tw-flex tw-items-center',
          'tw-w-[calc(100%-27px)]',
          'tw-border-solid tw-border-[1px] tw-border-cfo-grey-500 tw-rounded-[3px]',
          'tw-bg-cfo-grey-100',
          'tw-px-[12px] tw-py-[2px]',
          classes
        )}
        ref={containerRef}
      >
        <ClickAwayListener onClickAway={handleOnBlur} containerClass={clsx('tw-w-full')}>
          <SelectedTags
            tags={tags}
            tagKey={tagKey}
            onRemove={removeTag}
            onUpdate={handleUpdateTag}
            isInputFocused={isInputFocused}
            editMode={editMode}
          >
            <input
              className={clsx(
                'tw-border-none tw-outline-none',
                'tw-flex-1',
                'tw-w-[inherit]',
                'tw-p-0',
                // (!) is not removed here as it is to override the styles from vendor.css
                '!tw-bg-cfo-grey-100 tw-text-cfo-black'
              )}
              ref={inputRef}
              // autoFocus={isInputFocused}
              onKeyDown={handleKeyDown}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={handleOnBlur}
            />
          </SelectedTags>
        </ClickAwayListener>

        <Icon
          className={clsx('tw-cursor-pointer', 'tw-text-cfo-black')}
          icon={ICONS.dropdown}
          onClick={handleDropDownClick}
        />

        <Options
          showOptions={showOptions}
          options={options}
          tags={tags}
          hasMore={hasMore}
          refs={{
            lastElementRef,
            containerRef,
            popperRef,
          }}
          onSelectOption={onSelectOption}
          onPopperClickAway={handlePopperClickAway}
        />
      </div>
    </React.Fragment>
  );
};

export default UserTagInput;
